import { API_ENDPOINTS } from '../../../config';
import { getBearerAuthHeaders } from '../../../utils/authFetchHeaders';
import { getMyLiveAccounts } from './myDataApi';

/**
 * Data for the Trade Watcher dashboard widgets: the trader's own live accounts
 * (the board), their daily P&L history and recent alerts. Several widgets on one
 * dashboard share each request (one in flight, reused for a short while), so a
 * full Trade Watcher dashboard makes one board call, not five.
 */
const TTL_MS = 20 * 1000;
const cache = new Map();

function shared(key, load, ttl = TTL_MS) {
  const hit = cache.get(key);
  const now = Date.now();
  if (hit && (hit.pending || now - hit.at < ttl)) return hit.promise;
  const promise = load().then(
    (v) => { cache.set(key, { at: Date.now(), promise: Promise.resolve(v) }); return v; },
    (e) => { cache.delete(key); throw e; },
  );
  cache.set(key, { at: now, promise, pending: true });
  return promise;
}

async function getJson(url, fallback) {
  const res = await fetch(url, { headers: getBearerAuthHeaders() });
  const data = await res.json().catch(() => ({}));
  if (res.status === 403) {
    const err = new Error(data.error || 'Trade Watcher isn’t available on your plan.');
    err.notAvailable = true;
    throw err;
  }
  if (!res.ok || !data.success) throw new Error(data.error || fallback);
  return data;
}

/** The trader's own NinjaTrader accounts, live (connected, naked, day/open P&L). */
export function loadTwAccounts(email) {
  return shared(`acc:${email || ''}`, () => getMyLiveAccounts(email));
}

/** { days: [{ date, pnl, accounts }], accounts } for the last `days` days. */
export function loadTwHistory(days = 90) {
  return shared(`hist:${days}`, () => getJson(API_ENDPOINTS.tradeWatcherMyHistory(days), 'Couldn’t load your daily P&L'), 60 * 1000);
}

/** Recent alerts and events: [{ event, detail, ntAccount, at }]. */
export function loadTwEvents(limit = 30) {
  return shared(`ev:${limit}`, () => getJson(API_ENDPOINTS.tradeWatcherMyEvents(limit), 'Couldn’t load alerts').then((d) => d.events || []));
}

/** Everything that happened on one day (New York time): trade exits, alerts, disconnects. */
export function loadTwDayEvents(date) {
  return shared(`day:${date}`, () => getJson(API_ENDPOINTS.tradeWatcherMyEvents(500, date), 'Couldn’t load that day').then((d) => d.events || []), 60 * 1000);
}

/** Totals for the KPI strip, from the live accounts. */
export function summarizeAccounts(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const sum = (k) => Math.round(list.reduce((s, r) => s + (Number(r[k]) || 0), 0) * 100) / 100;
  const connected = list.filter((r) => r.connected === 'Yes').length;
  const naked = list.filter((r) => r.naked === 'Yes').length;
  const open = list.filter((r) => r.openPosition === true).length;
  const trading = list.filter((r) => r.trading === 'Yes').length;
  const clockOff = list.filter((r) => r.clockOk === 'No').length;
  return {
    accounts: list.length,
    connected,
    disconnected: list.length - connected,
    naked,
    open,
    trading,
    clockOff,
    dayPnl: sum('dayPnl'),
    openPnl: sum('openPnl'),
    runningPnl: sum('runningPnl'),
  };
}

/** Daily stats for a list of { date, pnl }: net, winning days, best, worst, streak. */
export function summarizeDays(days) {
  const list = (Array.isArray(days) ? days : []).filter((d) => Number.isFinite(Number(d.pnl)));
  const net = Math.round(list.reduce((s, d) => s + Number(d.pnl), 0) * 100) / 100;
  const wins = list.filter((d) => d.pnl > 0).length;
  const losses = list.filter((d) => d.pnl < 0).length;
  // Best is a winning day and worst a losing one: one green day isn't also "worst".
  const ups = list.filter((d) => d.pnl > 0);
  const downs = list.filter((d) => d.pnl < 0);
  const best = ups.length ? ups.reduce((a, b) => (b.pnl > a.pnl ? b : a)) : null;
  const worst = downs.length ? downs.reduce((a, b) => (b.pnl < a.pnl ? b : a)) : null;
  // Current streak: consecutive green (or red) days ending on the latest day.
  let streak = 0;
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const sign = Math.sign(list[i].pnl);
    if (!sign) break;
    if (!streak || Math.sign(streak) === sign) streak += sign;
    else break;
  }
  return {
    days: list.length,
    net,
    wins,
    losses,
    winRate: wins + losses ? wins / (wins + losses) : null,
    avg: list.length ? Math.round((net / list.length) * 100) / 100 : 0,
    best,
    worst,
    streak,
  };
}

/* Alerts ------------------------------------------------------------------ */

const lowerOf = (s) => String(s || '').toLowerCase();

/** What kind of event this is, from the add-on's free-text name. */
export function eventKindOf(name) {
  const t = lowerOf(name);
  if (t.includes('naked')) return 'naked';
  if (t.includes('auto') && t.includes('clos')) return 'autoClose';
  if (t.includes('reconnect') || t.includes('restored') || t === 'connected') return 'reconnect';
  if (t.includes('disconnect') || t.includes('lost') || t.includes('offline')) return 'disconnect';
  if (t.includes('closed') && t.includes('ninja')) return 'disconnect';
  if (t.includes('clock') || t.includes('time zone') || t.includes('timezone')) return 'clock';
  if (t.includes('size') || t.includes('instrument') || t.includes('timeframe') || t.includes('setting')) return 'settings';
  return 'other';
}

/** Plain-English detail ("Connection status: ConnectionLost" → "Connection lost"). */
export function cleanEventDetail(detail) {
  const d = String(detail || '').trim();
  if (!d) return '';
  return d
    .replace(/^connection status:\s*/i, '')
    .replace(/\bConnectionLost\b/g, 'Connection lost')
    .replace(/\bConnectionRestored\b/g, 'Connection restored')
    .replace(/^./, (c) => c.toUpperCase());
}

function minutesText(ms) {
  const m = Math.max(1, Math.round(ms / 60000));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return m % 60 && h < 10 ? `${h}h ${m % 60}m` : `${h}h`;
}

/**
 * The alerts feed: each disconnect paired with the reconnect that followed it
 * ("Disconnected · back after 4m"), reconnects folded in, newest first.
 * `important` drops the noise: short disconnects that came back within 15
 * minutes. `liveRows` says whether an unpaired disconnect is still offline.
 */
export function buildAlertFeed(events, liveRows = [], { important = true } = {}) {
  const list = (Array.isArray(events) ? events : [])
    .map((e) => ({ ...e, kind: eventKindOf(e.event), t: new Date(e.at).getTime() }))
    .filter((e) => Number.isFinite(e.t))
    .sort((a, b) => a.t - b.t);
  const offline = new Set((liveRows || []).filter((r) => r.connected !== 'Yes').map((r) => r.ntAccount));
  const used = new Set();
  const out = [];
  list.forEach((e, i) => {
    if (used.has(i)) return;
    if (e.kind === 'disconnect') {
      const j = list.findIndex((x, k) => k > i && !used.has(k) && x.ntAccount === e.ntAccount && x.kind === 'reconnect');
      if (j !== -1) {
        used.add(j);
        const gap = list[j].t - e.t;
        out.push({
          kind: 'resolved', ntAccount: e.ntAccount, at: e.at, t: e.t,
          title: 'Disconnected', detail: `Back after ${minutesText(gap)}`, gapMs: gap,
        });
      } else {
        const still = offline.has(e.ntAccount);
        out.push({
          kind: still ? 'disconnect' : 'resolved', ntAccount: e.ntAccount, at: e.at, t: e.t,
          title: 'Disconnected', detail: still ? 'Still offline' : (cleanEventDetail(e.detail) || 'Connection lost'),
        });
      }
      return;
    }
    if (e.kind === 'reconnect') {
      out.push({ kind: 'reconnect', ntAccount: e.ntAccount, at: e.at, t: e.t, title: 'Reconnected', detail: cleanEventDetail(e.detail) });
      return;
    }
    const titles = { naked: 'Naked position', autoClose: 'Auto-closed', clock: 'Time zone', settings: 'Settings changed' };
    out.push({
      kind: e.kind, ntAccount: e.ntAccount, at: e.at, t: e.t,
      title: titles[e.kind] || e.event, detail: cleanEventDetail(e.detail),
    });
  });
  const shown = important
    ? out.filter((x) => x.kind !== 'reconnect' && !(x.kind === 'resolved' && (x.gapMs == null || x.gapMs < 15 * 60000)))
    : out;
  return shown.sort((a, b) => b.t - a.t);
}

/** What needs a look on each account right now, from the live board. */
export function accountIssues(row) {
  if (!row) return [];
  const out = [];
  if (row.naked === 'Yes') out.push({ kind: 'naked', text: 'Naked: open position without a protective stop' });
  if (row.connected !== 'Yes') {
    const since = row.offlineSince || row.disconnectedSince;
    out.push({ kind: 'offline', text: since ? `Offline since ${new Date(since).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : 'Offline' });
  }
  if (row.clockOk === 'No') out.push({ kind: 'clock', text: `NinjaTrader’s time zone is ${row.clockTz || 'not Eastern'}. Set it to Eastern in Tools → Options → General.` });
  return out;
}

/** Reset for tests. */
export function _clearTwCache() {
  cache.clear();
}
